import { useEffect, useRef, useState } from 'react';
import { useGameStore } from '../stores/gameStore';
import { calcTotals, formatMb, formatMbit, formatMhz, xpProgress, usedHddMb, filesHddMb, runningRamMb, runningCpuMhz, infectionsHddMb, infectionsRamMb, infectionsCpuMhz, encryptedHdUsedMb } from '../types';
import IpChangePanel from './IpChangePanel';
import FormatNodePanel from './FormatNodePanel';

export default function PlayerStats() {
  const { player, loadPlayer, isLoading, infections } = useGameStore();

  useEffect(() => {
    loadPlayer();
  }, [loadPlayer]);

  // Flash the stat that just went up (credits / level / reputation), so a reward is
  // noticed even when it lands while the player is looking at another panel.
  const prev = useRef<{ credits: number; level: number; reputation: number } | null>(null);
  const [bumped, setBumped] = useState<Record<string, boolean>>({});
  useEffect(() => {
    if (!player) return;
    const now = { credits: player.credits, level: player.level, reputation: player.reputation };
    const before = prev.current;
    prev.current = now;
    if (!before) return; // first load is not a gain
    const hits: Record<string, boolean> = {};
    if (now.credits    > before.credits)    hits.Credits    = true;
    if (now.level      > before.level)      hits.Level      = true;
    if (now.reputation > before.reputation) hits.Reputation = true;
    if (Object.keys(hits).length === 0) {
      // No gain this time. Clear any leftover flash — a player refresh landing inside
      // the 600ms window would otherwise cancel the reset timer and leave the class
      // stuck on, which would stop the NEXT gain from re-triggering the animation.
      // Returning the same object when already empty avoids a pointless re-render.
      setBumped(b => (Object.keys(b).length ? {} : b));
      return;
    }
    setBumped(hits);
    const id = setTimeout(() => setBumped({}), 600);
    return () => clearTimeout(id);
  }, [player]);

  if (isLoading && !player) return <div className="loading">Loading stats...</div>;
  if (!player) return <div className="error">Failed to load player stats</div>;

  const totals = calcTotals(player.servers);
  const xp = xpProgress(player.experience, player.level);
  const sw = player.software;
  // Infections planted on this node by attackers also occupy its HDD/RAM/CPU.
  const inf     = infections;
  const hddUsed = usedHddMb(sw) + filesHddMb(player.files ?? []) + infectionsHddMb(inf);
  const ramUsed = runningRamMb(sw) + infectionsRamMb(inf);
  const cpuUsed = runningCpuMhz(sw) + infectionsCpuMhz(inf);

  const stats = [
    { label: 'Level',      value: player.level },
    { label: 'Credits',    value: player.credits.toLocaleString() },
    { label: 'Reputation', value: player.reputation.toLocaleString() },
  ];

  const rowStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '0.5rem 0',
    fontFamily: 'monospace',
    fontSize: '0.875rem',
  };

  return (
    <div className="card">
      <div className="card-header">
        <h2 className="card-title">{player.username}</h2>
      </div>

      <div className="stats-grid">
        {stats.map(s => (
          <div key={s.label} className="stat-card">
            <div className={`stat-value${bumped[s.label] ? ' fx-bump' : ''}`}>{s.value}</div>
            <div className="stat-label">{s.label}</div>
          </div>
        ))}
      </div>

      <div style={{ marginTop: '1rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.3rem', fontFamily: 'monospace', fontSize: '0.8rem' }}>
          <span style={{ color: '#888' }}>Experience</span>
          <span style={{ color: '#888' }}>{xp.current.toLocaleString()} / {xp.needed.toLocaleString()} XP</span>
        </div>
        <div style={{ height: '6px', background: '#1a1a2e', borderRadius: '3px', overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${xp.pct}%`, background: 'var(--primary-color)', borderRadius: '3px', transition: 'width 0.4s ease' }} />
        </div>
        <div style={{ textAlign: 'right', marginTop: '0.2rem', fontFamily: 'monospace', fontSize: '0.75rem', color: '#555' }}>
          {xp.pct}% to level {player.level + 1}
        </div>
      </div>

      <div style={{ marginTop: '1.25rem' }}>
        <h3 style={{ color: 'var(--primary-color)', marginBottom: '0.75rem', fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Network
        </h3>
        <div style={{ ...rowStyle, borderBottom: '1px solid #1a1a2e', flexWrap: 'wrap', gap: '0.25rem' }}>
          <span style={{ color: '#888' }}>IP Address</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
            <span style={{ color: '#4ade80' }}>{player.ipAddress || '—'}</span>
            <IpChangePanel compact />
          </div>
        </div>
        <div style={{ ...rowStyle, borderBottom: '1px solid #1a1a2e' }}>
          <span style={{ color: '#888' }}>Line Speed</span>
          <span>{formatMbit(player.internetSpeedMBit)}</span>
        </div>
        <div style={{ ...rowStyle, flexWrap: 'wrap', gap: '0.25rem' }}>
          <span style={{ color: '#888' }}>Node</span>
          <FormatNodePanel compact />
        </div>
      </div>

      <div style={{ marginTop: '1.25rem' }}>
        <h3 style={{ color: 'var(--primary-color)', marginBottom: '0.75rem', fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Infrastructure
        </h3>
        {([
          { label: 'HDD', used: hddUsed, total: totals.hdd, fmt: formatMb },
          { label: 'RAM', used: ramUsed,  total: totals.ram, fmt: formatMb },
          { label: 'CPU', used: cpuUsed,  total: totals.cpu, fmt: formatMhz },
        ] as const).map(({ label, used, total, fmt }) => {
          const pct = total > 0 ? Math.min(100, (used / total) * 100) : 0;
          const color = pct > 90 ? '#f87171' : pct > 70 ? '#fbbf24' : '#4ade80';
          return (
            <div key={label} style={{ marginBottom: '0.6rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontFamily: 'monospace', fontSize: '0.78rem', marginBottom: '0.2rem' }}>
                <span style={{ color: '#888' }}>{label}</span>
                <span style={{ color }}>{fmt(used)} / {fmt(total)}</span>
              </div>
              <div style={{ height: '5px', background: '#1a1a2e', borderRadius: '3px', overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: '3px', transition: 'width 0.3s ease' }} />
              </div>
            </div>
          );
        })}
      </div>

      {player.encryptedHd && (
        <div style={{ marginTop: '1.25rem' }}>
          <h3 style={{ color: 'var(--primary-color)', marginBottom: '0.75rem', fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Encrypted HD
          </h3>
          {(() => {
            const cap  = player.encryptedHd.capacityMb;
            const used = encryptedHdUsedMb(player.encryptedHd);
            const pct  = cap > 0 ? Math.min(100, (used / cap) * 100) : 0;
            const color = pct > 90 ? '#f87171' : pct > 70 ? '#fbbf24' : '#a78bfa';
            return (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontFamily: 'monospace', fontSize: '0.78rem', marginBottom: '0.2rem' }}>
                  <span style={{ color: '#888' }}>Backup space</span>
                  <span style={{ color }}>{formatMb(used)} / {formatMb(cap)}</span>
                </div>
                <div style={{ height: '5px', background: '#1a1a2e', borderRadius: '3px', overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: '3px', transition: 'width 0.3s ease' }} />
                </div>
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
}
